import React, { useEffect, useState } from "react";

import {
    Search,
    Server,
    Fan,
    Zap,
    Thermometer,
    Box,
    Cpu,
    EthernetPort,
    HelpCircle,
    Filter,
    HardDrive
} from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";

interface SnmpHardwareProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
}

interface HardwareEntity {
    class: string;
    description: string;
    index: number;
    model_name?: string;
    name: string;
    serial_number?: string;
    firmware_revision?: string;
    software_revision?: string;
    hardware_revision?: string;
}

const SnmpHardware: React.FC<SnmpHardwareProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
}) => {
    const [hardwareData, setHardwareData] = useState<HardwareEntity[]>([]);
    const [filteredData, setFilteredData] = useState<HardwareEntity[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedClass, setSelectedClass] = useState<string>("all");

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const params = {
                query: JSON.stringify({
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id,
                })
            };
            const response = await baseapi.get('/snmphardwareinfo', { params });
            if (response.data.status) {
                // Support both array directly or wrapped in data object
                const data = Array.isArray(response.data.data) ? response.data.data : [];
                // If data comes as [[...]], flatten it, otherwise use as is
                const cleanData = Array.isArray(data[0]) ? data.flat() : data;

                setHardwareData(cleanData);
                setFilteredData(cleanData);
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    // Filtering Logic
    useEffect(() => {
        let result = hardwareData;

        // Filter by Search Term
        if (searchTerm) {
            const lowerTerm = searchTerm.toLowerCase();
            result = result.filter(item =>
                item.name?.toLowerCase().includes(lowerTerm) ||
                item.description?.toLowerCase().includes(lowerTerm) ||
                item.serial_number?.toLowerCase().includes(lowerTerm) ||
                item.model_name?.toLowerCase().includes(lowerTerm)
            );
        }

        // Filter by Class
        if (selectedClass !== "all") {
            result = result.filter(item => item.class === selectedClass);
        }

        setFilteredData(result);
    }, [searchTerm, selectedClass, hardwareData]);

    // Get Unique Classes for Filter Dropdown
    const uniqueClasses = Array.from(new Set(hardwareData.map(item => item.class))).sort();

    // Helper: Get Icon based on Class
    const getClassIcon = (cls: string) => {
        switch (cls.toLowerCase()) {
            case 'chassis': return <Server className="w-4 h-4 text-blue-400" />;
            case 'fan': return <Fan className="w-4 h-4 text-cyan-400" />;
            case 'powersupply': return <Zap className="w-4 h-4 text-yellow-400" />;
            case 'sensor': return <Thermometer className="w-4 h-4 text-red-400" />;
            case 'module': return <Cpu className="w-4 h-4 text-green-400" />;
            case 'port': return <EthernetPort className="w-4 h-4 text-orange-400" />;
            case 'storage': return <HardDrive className="w-4 h-4 text-purple-400" />;
            case 'container': return <Box className="w-4 h-4 text-indigo-400" />;
            default: return <HelpCircle className="w-4 h-4 text-gray-400" />;
        }
    };

    // Helper: Format Class Name
    const formatClass = (cls: string) => {
        return cls.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
    };

    if (isLoading) {
        return <div className="p-8 text-center text-gray-400 animate-pulse">Loading hardware inventory...</div>;
    }

    if (hardwareData.length === 0) {
        return (
            <div className="p-8 flex flex-col items-center justify-center text-gray-400 bg-gray-800 rounded-lg border border-gray-700">
                <Box className="w-12 h-12 mb-2 text-gray-600" />
                <p>No physical entities found.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4 animate-fade-in h-full flex flex-col">
            {/* Controls Bar */}
            <div className="flex flex-col sm:flex-row justify-between gap-4 bg-gray-900 p-1 rounded-lg">

                {/* Search */}
                <div className="relative w-64">
                    <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        placeholder="Search by name, serial, description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                    />
                </div>

                {/* Class Filter */}
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <select
                        value={selectedClass}
                        onChange={(e) => setSelectedClass(e.target.value)}
                        className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-md px-3 py-2 focus:outline-none focus:border-orange-500  "
                    >
                        <option value="all">All Components</option>
                        {uniqueClasses.map(cls => (
                            <option key={cls} value={cls}>{formatClass(cls)}</option>
                        ))}
                    </select>
                    <span className="text-xs text-gray-500 bg-gray-800 px-2 py-1 rounded border border-gray-700">
                        {filteredData.length} Items
                    </span>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-gray-900 rounded-lg border border-gray-700 overflow-hidden flex-1">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                        <thead className="bg-gray-800 text-gray-400 font-medium uppercase text-xs">
                            <tr>
                                <th className="px-6 py-3">Component Name</th>
                                <th className="px-6 py-3">Type</th>
                                <th className="px-6 py-3">Description</th>
                                <th className="px-6 py-3">Model / Serial</th>
                                <th className="px-6 py-3 text-right text-xs">Index</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800 bg-gray-900">
                            {filteredData.length > 0 ? (
                                filteredData.map((item, idx) => (
                                    <tr key={`${item.index}-${idx}`} className="hover:bg-gray-800/50 transition-colors">

                                        {/* Name */}
                                        <td className="px-6 py-3 font-medium text-white">
                                            {item.name}
                                        </td>

                                        {/* Class / Type */}
                                        <td className="px-6 py-3">
                                            <div className="flex items-center gap-2">
                                                {getClassIcon(item.class)}
                                                <span className="text-gray-300 capitalize text-xs bg-gray-800 px-2 py-0.5 rounded border border-gray-700">
                                                    {formatClass(item.class)}
                                                </span>
                                            </div>
                                        </td>

                                        {/* Description */}
                                        <td className="px-6 py-3 text-gray-400 max-w-xs truncate" title={item.description}>
                                            {item.description || <span className="italic text-gray-600">-</span>}
                                        </td>

                                        {/* Model & Serial */}
                                        <td className="px-6 py-3">
                                            <div className="flex flex-col">
                                                {item.model_name && (
                                                    <span className="text-gray-200 text-xs mb-0.5">
                                                        <span className="text-gray-500">Model:</span> {item.model_name}
                                                    </span>
                                                )}
                                                {item.serial_number ? (
                                                    <span className="text-orange-300 font-mono text-xs">
                                                        SN: {item.serial_number}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-600 text-xs italic">No Serial</span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Index */}
                                        <td className="px-6 py-3 text-right text-gray-500 font-mono text-xs">
                                            #{item.index}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                        <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
                                        <p>No hardware found matching filters.</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SnmpHardware;
